import { useState } from "react";
import { supabase } from "./supabase";

const s = {
  wrapper: {
    backgroundColor: "#111",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center"
  },
  container: {
    backgroundColor: "#111",
    minHeight: "100vh",
    color: "#f0f0f0",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    width: "100%",
    maxWidth: "480px",
    display: "flex",
    flexDirection: "column",
    padding: "40px 24px"
  },
  logo: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#fff",
    letterSpacing: "-0.5px",
    marginBottom: "48px"
  },
  logoAccent: {
    color: "#8B4513"
  },
  headline: {
    fontSize: "26px",
    fontWeight: "600",
    color: "#fff",
    lineHeight: "1.3",
    marginBottom: "10px"
  },
  subtext: {
    fontSize: "15px",
    color: "#777",
    lineHeight: "1.6",
    marginBottom: "32px"
  },
  input: {
    width: "100%",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    color: "#f0f0f0",
    fontSize: "16px",
    padding: "16px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    marginBottom: "12px",
    outline: "none"
  },
  codeInput: {
    width: "100%",
    backgroundColor: "#1a1a1a",
    border: "1px solid #2a2a2a",
    borderRadius: "12px",
    color: "#f0f0f0",
    fontSize: "32px",
    fontWeight: "600",
    letterSpacing: "10px",
    padding: "16px",
    fontFamily: "inherit",
    boxSizing: "border-box",
    marginBottom: "12px",
    outline: "none",
    textAlign: "center"
  },
  button: {
    width: "100%",
    backgroundColor: "#8B4513",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontSize: "17px",
    fontWeight: "600",
    padding: "16px",
    cursor: "pointer"
  },
  buttonDisabled: {
    backgroundColor: "#2a1e17",
    color: "#555",
    cursor: "not-allowed"
  },
  backButton: {
    background: "none",
    border: "none",
    color: "#555",
    fontSize: "14px",
    cursor: "pointer",
    fontFamily: "inherit",
    padding: "0",
    marginTop: "16px",
    display: "block"
  },
  error: {
    color: "#ff6b6b",
    fontSize: "14px",
    marginTop: "10px"
  }
};

// Supabase's default email OTP length changed to 8 digits on newer projects (used to be 6).
// This must match whatever length is actually configured in Supabase Dashboard →
// Authentication → Sign In / Providers → Email → Email OTP length.
const OTP_LENGTH = 8;

export default function AuthScreen() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("email"); // "email" | "code"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSend() {
    if (!email.trim()) return;
    setLoading(true);
    setError("");

    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim()
    });

    if (error) {
      setError("Something went wrong. Try again.");
      setLoading(false);
    } else {
      setStep("code");
      setLoading(false);
    }
  }

  async function handleVerify() {
    if (code.trim().length < OTP_LENGTH) return;
    setLoading(true);
    setError("");

    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: "email"
    });

    if (error) {
      setError("That code didn't work. Check your email and try again.");
      setLoading(false);
    } else {
      // Auth state change in App.js will handle the rest
      setLoading(false);
    }
  }

  function handleEmailKeyDown(e) {
    if (e.key === "Enter") handleSend();
  }

  function handleCodeKeyDown(e) {
    if (e.key === "Enter") handleVerify();
  }

  return (
    <div style={s.wrapper}>
      <div style={s.container}>
        <div style={s.logo}>
          da<span style={s.logoAccent}>vino</span>
        </div>

        {step === "email" ? (
          <>
            <div style={s.headline}>Enter your email to continue.</div>
            <div style={s.subtext}>
              We'll send you a code — no password needed. Already have a profile?
              It'll be waiting for you. First time? We'll get you set up right after.
            </div>
            <input
              style={s.input}
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              autoFocus
            />
            <button
              style={{
                ...s.button,
                ...(loading || !email.trim() ? s.buttonDisabled : {})
              }}
              onClick={handleSend}
              disabled={loading || !email.trim()}
            >
              {loading ? "Sending..." : "Send code"}
            </button>
            {error && <div style={s.error}>{error}</div>}
          </>
        ) : (
          <>
            <div style={s.headline}>Check your email.</div>
            <div style={s.subtext}>
              We sent an {OTP_LENGTH}-digit code to <strong style={{ color: "#f0f0f0" }}>{email}</strong>. Enter all {OTP_LENGTH} digits below.
            </div>
            <input
              style={{ ...s.codeInput, letterSpacing: "6px" }}
              type="number"
              inputMode="numeric"
              placeholder={"0".repeat(OTP_LENGTH)}
              value={code}
              onChange={e => setCode(e.target.value.slice(0, OTP_LENGTH))}
              onKeyDown={handleCodeKeyDown}
              autoFocus
            />
            <button
              style={{
                ...s.button,
                ...(loading || code.trim().length < OTP_LENGTH ? s.buttonDisabled : {})
              }}
              onClick={handleVerify}
              disabled={loading || code.trim().length < OTP_LENGTH}
            >
              {loading ? "Verifying..." : code.trim().length < OTP_LENGTH ? `Enter ${OTP_LENGTH - code.trim().length} more digit${OTP_LENGTH - code.trim().length === 1 ? "" : "s"}` : "Sign in"}
            </button>
            {error && <div style={s.error}>{error}</div>}
            <button
              style={s.backButton}
              onClick={() => { setStep("email"); setCode(""); setError(""); }}
            >
              ← Use a different email
            </button>
          </>
        )}
      </div>
    </div>
  );
}
